import { Box, Typography } from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import useAPI from "../../../../../../../api/hooks/useAPI";
import useColors from "../../../../../../../api/hooks/useColors";
import useSmartAndRegularAssets from "../../../../../../../api/hooks/useSmartAndRegularAssets";
import { controlKeys, entityKeys } from "../../../../../../../api/queryKeys";
import LoadingIcon from "../../../../../../../components/LoadingIcon";
import iconMapping from "../../../../../../../icons/IconMapping";
import ControlEntitiesActionButtons from "../../shared/ControlEntitiesActionButtons";
import SearchTextField from "../../shared/SearchTextField";
import EntityTypeAndStatusTabbedTable from "../../tables/EntityTypeAndStatusTabbedTable";
import { MisconfigStatusTab } from "../../tables/MisconfigStatusTabs";
import MisconfigurationEvidence from "../MisconfigurationEvidence";
import { groupEntitiesByTypeAndStatus } from "./affectedEntitiesSectionUtils";
import getMisconfigurationTableColumns from "./affectedEntitiesTableColumns";

export default function AffectedEntitiesSection({
  fullControl,
  misconfiguration,
}) {
  const colors = useColors();
  const { getEntitiesByControlAndMisconfig, trackEvent } = useAPI();
  const [searchText, setSearchText] = useState("");

  const [selectedEntityTypeTab, setSelectedEntityTypeTab] = useState(null);
  const [selectedStatusTab, setSelectedStatusTab] = useState(null);
  const [checkedEntityIDs, setCheckedEntityIDs] = useState([]);
  const [appliedFilters, setAppliedFilters] = useState({
    labels: {},
    assets: {},
  });
  const { regularAndActiveSmartAsset: assets } =
    useSmartAndRegularAssets(false);

  const affectedEntitiesByTypeQuery = useQuery({
    queryKey: controlKeys.controlMisconfigurationAffectedEntities(
      fullControl.control_id,
      misconfiguration.misconfiguration_id
    ),
    queryFn: () =>
      getEntitiesByControlAndMisconfig(
        fullControl.control_id,
        misconfiguration.misconfiguration_id
      ),

    select: (response) => response?.data,
  });

  const queryClient = useQueryClient();
  const terminatedEntities = queryClient.getQueryData(
    entityKeys.terminatedEntities()
  ).data;

  const entitiesByTypeAndStatus = affectedEntitiesByTypeQuery.isSuccess
    ? groupEntitiesByTypeAndStatus(
        affectedEntitiesByTypeQuery.data,
        misconfiguration,
        searchText,
        appliedFilters,
        assets,
        fullControl.control_id,
        trackEvent
      )
    : {};

  if (!entitiesByTypeAndStatus) {
    return (
      <Typography variant="body2" color={colors.primary}>
        Error loading this content...
      </Typography>
    );
  }

  if (
    affectedEntitiesByTypeQuery.isSuccess &&
    affectedEntitiesByTypeQuery.data &&
    !selectedEntityTypeTab
  ) {
    const initialSelectedTab = Object.keys(entitiesByTypeAndStatus)[0];
    setSelectedEntityTypeTab(initialSelectedTab);
    setSelectedStatusTab(MisconfigStatusTab.INCLUDED);
  }

  const columns = getMisconfigurationTableColumns(
    misconfiguration,
    selectedEntityTypeTab,
    selectedStatusTab,
    colors,
    terminatedEntities
  );

  const Icon =
    iconMapping[misconfiguration.entity_type] || iconMapping["Internet"];

  const handleCheckedChange = (newCheckedEntityIDs) => {
    trackEvent(
      `User checked an affected entity of misconfiguration ${misconfiguration.entity_id}`
    );
    setCheckedEntityIDs(newCheckedEntityIDs);
  };

  return (
    <Box>
      {affectedEntitiesByTypeQuery.isLoading ? (
        <LoadingIcon size={22} />
      ) : affectedEntitiesByTypeQuery.isSuccess &&
        selectedEntityTypeTab &&
        affectedEntitiesByTypeQuery.data ? (
        <Box display="flex" flexDirection="column" gap={2}>
          <Box display="flex" alignItems="center" gap={1}>
            <Icon
              style={{
                width: "20px",
                height: "20px",
              }}
            />
            <Typography
              variant="subtitle1"
              color={colors.primary}
              lineHeight="20px"
            >
              {misconfiguration.display_name}
            </Typography>
          </Box>
          <Box display="flex" width="100%" justifyContent="space-between">
            <SearchTextField
              searchText={searchText}
              setSearchText={setSearchText}
            />
            <ControlEntitiesActionButtons
              fullControl={fullControl}
              entitiesByTypeAndStatus={entitiesByTypeAndStatus}
              checkedEntityIDs={checkedEntityIDs}
              selectedEntityTypeTab={selectedEntityTypeTab}
              selectedStatusTab={selectedStatusTab}
              parentMisconfiguration={misconfiguration}
              setAppliedFilters={setAppliedFilters}
              appliedFilters={appliedFilters}
            />
          </Box>
          <EntityTypeAndStatusTabbedTable
            entitiesByTypeAndStatus={entitiesByTypeAndStatus}
            columns={columns}
            selectedEntityTypeTab={selectedEntityTypeTab}
            setSelectedEntityTypeTab={setSelectedEntityTypeTab}
            selectedStatusTab={selectedStatusTab}
            setSelectedStatusTab={setSelectedStatusTab}
            handleCheckedChange={handleCheckedChange}
          />
          {Object.keys(misconfiguration?.evidence ?? {}).length > 0 && (
            <Box
              sx={{
                width: "100%",
                boxShadow: " 0px 0px 2px 1px rgba(203, 203, 203, 0.50)",
                backgroundColor: colors.common_white,
                borderRadius: "10px",
                padding: 3,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <MisconfigurationEvidence
                controlId={fullControl?.control_id}
                evidence={misconfiguration?.evidence}
              />
            </Box>
          )}
        </Box>
      ) : (
        "Error loading this content..."
      )}
    </Box>
  );
}
